import type { EventBus } from '../core/events'
import { RNG } from '../core/rng'
import type { Tuning } from '../config/tuning'
import type { LevelData } from '../world/level'
import { TileMap } from '../world/tiles'
import type { Entity } from './entity'

export interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  size: number
  color: string
}

export type GameMode = 'campaign' | 'endless'

/** neutral per-floor directive modifiers (see World.floorMods) */
function defaultFloorMods() {
  return {
    directive: null as string | null,
    scoreMul: 1,
    foodHealMul: 1,
    enemySpeedMul: 1,
    generatorIntervalMul: 1,
    /** world px; 0 disables the blackout overlay */
    blackoutRadius: 0
  }
}

/**
 * Simulation state hub. Systems are pure-ish functions over (World, dt).
 * Nothing here assumes exactly one player: `players()` is a list; camera and
 * HUD pick players()[0] in v1 only.
 */
export class World {
  entities: Entity[] = []
  map!: TileMap
  level!: LevelData
  rng: RNG
  levelRng: RNG
  /** cosmetic-only stream: particles must never perturb gameplay randomness */
  fxRng: RNG

  // run state (arcade-style shared pool)
  score = 0
  floor = 1
  mode: GameMode = 'campaign'
  seed: number
  /**
   * Run-scoped requisition modifiers, written by the between-floor
   * requisition flow (game.ts) and read at the stat sites (fire cooldown and
   * projectile damage in playerSystem, vitals cap in the loader/respawn,
   * charge cap in pickups). The World lives exactly one run, so these reset
   * with it; ranks survive death within the run — equipment, not consumables.
   */
  mods = { fireCooldownMul: 1, damageMul: 1, maxHpBonus: 0, maxMagicBonus: 0 }
  /**
   * Floor-scoped overtime-directive modifiers, written by game.ts right after
   * an endless floor loads and reset by beginLevel — one floor, one directive.
   * Read at the same stat sites style as `mods`: score in addScore, food heal
   * in pickups, enemy speed in ai, assembler cycle in generators, blackout
   * radius (px) in the renderer.
   */
  floorMods = defaultFloorMods()

  /**
   * Run-scoped amenity perks (ids from src/game/amenities.ts), written by the
   * between-floor panel flow in game.ts, read at the effect hook sites.
   * Survives floor loads; the World lives exactly one run.
   */
  amenities = new Set<string>()
  /** LINGERING SANCTION damage fields (sim state; cleared per level) */
  fields: { x: number; y: number; r: number; t: number; tickT: number }[] = []

  // level state
  levelTime = 0
  exitOpen = true
  cleared = false
  bossActive = false
  auditorQueue: { delay: number }[] = []

  // presentation state written by simulation, read by renderer
  shake = 0
  shakeEnabled = true
  particles: Particle[] = []
  reducedMotion = false

  private pendingAdd: Entity[] = []

  constructor(
    public bus: EventBus,
    public tuning: Tuning,
    seed: number
  ) {
    this.seed = seed
    this.rng = new RNG(seed)
    this.levelRng = this.rng.fork(0)
    this.fxRng = this.rng.fork(0x0f0f)
  }

  add(e: Entity): Entity {
    this.pendingAdd.push(e)
    return e
  }

  /** flush queued adds and drop dead entities — call once per tick, last */
  sweep(): void {
    if (this.pendingAdd.length) {
      this.entities.push(...this.pendingAdd)
      this.pendingAdd = []
    }
    let w = 0
    for (let i = 0; i < this.entities.length; i++) {
      const e = this.entities[i]!
      if (!e.dead) this.entities[w++] = e
    }
    this.entities.length = w
  }

  players(): Entity[] {
    return this.entities.filter((e) => e.kind === 'player' && !e.dead)
  }

  enemies(): Entity[] {
    return this.entities.filter((e) => (e.kind === 'enemy' || e.kind === 'boss') && !e.dead)
  }

  generators(): Entity[] {
    return this.entities.filter((e) => e.kind === 'generator' && !e.dead)
  }

  nearestPlayer(x: number, y: number): Entity | null {
    let best: Entity | null = null
    let bestD = Infinity
    for (const p of this.players()) {
      const d = (p.x - x) ** 2 + (p.y - y) ** 2
      if (d < bestD) {
        bestD = d
        best = p
      }
    }
    return best
  }

  aliveChildrenOf(genId: number): number {
    let n = 0
    for (const e of this.entities) {
      if (!e.dead && e.parentGen === genId) n++
    }
    return n
  }

  addScore(delta: number): void {
    // directive premiums apply to earnings only — spending is never discounted
    const applied = delta > 0 ? Math.round(delta * this.floorMods.scoreMul) : delta
    this.score += applied
    this.bus.emit('score_changed', { score: this.score, delta: applied })
  }

  /** load a level: reset per-level state, build the tilemap; caller populates entities */
  beginLevel(level: LevelData, floor: number): void {
    this.level = level
    this.floor = floor
    this.map = new TileMap(level, this.tuning.hazards.destructibleWallHp)
    this.entities = []
    this.pendingAdd = []
    this.particles = []
    this.levelTime = 0
    this.cleared = false
    this.bossActive = level.isBossGate
    this.exitOpen = level.exit.type === 'reach'
    this.auditorQueue = (level.auditorSpawns ?? []).map((a) => ({ ...a }))
    this.fields = []
    this.floorMods = defaultFloorMods()
    this.levelRng = this.rng.fork(floor * 7919)
  }

  burst(x: number, y: number, color: string, count: number, speed = 120, life = 0.5, size = 3): void {
    // fxRng only: reduced motion changes the draw count, and an accessibility
    // setting must never shift the gameplay streams
    const n = this.reducedMotion ? Math.ceil(count / 3) : count
    for (let i = 0; i < n; i++) {
      const a = this.fxRng.range(0, Math.PI * 2)
      const s = this.fxRng.range(speed * 0.3, speed)
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life,
        maxLife: life,
        size: this.fxRng.range(size * 0.6, size * 1.4),
        color
      })
    }
  }

  addShake(amount: number): void {
    if (!this.shakeEnabled) return
    if (this.reducedMotion) amount *= 0.25
    this.shake = Math.min(14, this.shake + amount)
  }
}
